
	import { Component, Vue, Watch } from "vue-property-decorator";
	import { namespace, Getter, Action, Mutation } from "vuex-class";

	import Sessions from "@/components/Sessions.vue";
	import Cameras from "@/components/Cameras.vue";
	import LinkingCamera from "@/components/LinkingCamera.vue";
	import ManualRaise from "@/components/ManualRaise.vue";
	import { SessionResource } from '@/store/sessions/types';
	import { EventDetails } from "@/store/site-monitor/types";
	import organizationApi from "@/services/api.organizationSettings";

	const SiteMonitor = namespace("siteMonitor");
	const SMCameras = namespace("siteMonitorCameras");

	@Component({
		components: {
			cameras: Cameras,
			session: Sessions,
			"link-camera": LinkingCamera,
			"manual-raise": ManualRaise,
		}
	})
	export default class SiteMonitorCameras extends Vue {
		@Getter isLoggedIn: any;
		@Getter getActiveRegion;
		@Getter getFeature: (featureName: string[]) => boolean;
		@Action updateActiveRegion: any;

		@SMCameras.Mutation setMediaMatrixEventId: any; // should be proxied through action
		@SiteMonitor.Mutation private setActivity: () => void;
		@SiteMonitor.Getter("getEventDetails") eventDetails: EventDetails;
		@SMCameras.Mutation setLayoutIndex: any;
		@SMCameras.Getter("getLayoutIndex") getLayoutIndex!: number;

		private auditServiceSessionResourceId = SessionResource.AuditServiceSession;
		private deviceServiceSessionResourceId = SessionResource.DeviceServiceSession;
		private currentEventGroupId: number = 0;
		private currentEventGroupLayoutPref: number = -1;

		@Watch("isLoggedIn")
		onIsLoggedInChanged(value: boolean, oldValue: boolean) {
			if (!value) {
				console.error("auto close media matrix - Site Monitor Cameras: User is not logged in, closing window.");
				window.close();
			}
		}

		get eventId() {
			try {
				return this.eventDetails == null ? 0 : this.eventDetails.eventID;
			} catch (err) {
				return 0;
			}
		}

		private async mounted() {
			this.$el.addEventListener("mousedown", (e: MouseEvent) => { this.setActivity(); })
			if (this.isMediaMatrixLayoutSetupEnabled && this.eventDetails && this.eventDetails.groupID) {
				await this.getAreaMediaMatrixLayout();
			}
		}

		public beforeDestroy() {
			this.$el.removeEventListener("mousedown", (e: MouseEvent) => { this.setActivity(); })
		}

		private async getAreaMediaMatrixLayout(): Promise<any> {
			try {
				const layout = await organizationApi.GetMediaMatrixLayout(this.eventDetails.groupID);
				this.setLayoutIndex(layout);
				this.currentEventGroupLayoutPref = layout;
			} catch (error) {
				console.error('Failed to get media matrix layout:', error);
			}
		}

		private created() {
			//As this window is in a seperate window we need to retrieve the active region from the shared store
			//and call the update ActiveRegion action which will update the api's base url to point at the correct region
			this.updateActiveRegion(this.getActiveRegion);
			window.zE('webWidget', 'hide');
		}

		public get mediaMatrixEnabled() {
			return this.getFeature(["Alarms", "MediaMatrix"]);
		}

		public get linkedCamerasEnabled() {
			return this.getFeature(["Alarms", "MediaMatrix", "LinkedCameras"]);
		}

		public get isCloseOnEventEndEnabled() {
			return this.getFeature(["Alarms", "MediaMatrix", "CloseOnEnd"]);
		}

		private get isMediaMatrixLayoutSetupEnabled(): boolean {
            return this.getFeature(["Alarms", "MediaMatrix", "DefaultMediaMatrixLayout"]);
        }

		@Watch("eventDetails")
		private async onEventDetailsChanged(newValue): Promise<void> {
			if (this.isCloseOnEventEndEnabled) {
				if (!newValue || newValue.eventID === 0) {
					window.close();
				}
			}

			if (this.isMediaMatrixLayoutSetupEnabled
				&& newValue
				&& (newValue.eventId !== 0
						&& (newValue.groupID !== this.currentEventGroupId
							|| (
								this.currentEventGroupLayoutPref !== this.getLayoutIndex
								&& newValue.groupID === this.currentEventGroupId
							)
						)
					)
			) {
				this.currentEventGroupId = newValue.groupID;
				await this.getAreaMediaMatrixLayout();
			}
		}
	}
